<template>
  <div
    class="py-3 px-2 border-bottom"
    :class="{ 'd-flex align-items-center': isEditable }"
  >
    <template v-if="isEditable">
      <div class="drag-handle pr-1">
        <font-awesome-icon
          title="Drag to reorder"
          :icon="['fal', 'grip-lines-vertical']"
          size="2x"
        />
      </div>
      <div class="flex-grow-1">
        <b-form-row>
          <b-col class="numeric-input wide pb-2">
            <b-input-group>
              <template v-slot:append>
                <b-input-group-text>&times;</b-input-group-text>
              </template>
              <b-form-input
                type="number"
                min="1"
                :value="cardQuantity"
                @input="$emit('update:cardQuantity', parseInt($event))"
              />
            </b-input-group>
          </b-col>

          <b-col class="pb-2">
            <b-form-input
              :value="cardTitle"
              placeholder="Card title"
              @input="$emit('update:cardTitle', $event)"
            />
          </b-col>
          <b-col class="fit-content pb-2" order-md="6">
            <b-button
              variant="outline-danger"
              @click="$emit('delete:card', $event)"
            >
              <font-awesome-icon :icon="['fas', 'trash-alt']" />
            </b-button>
          </b-col>
          <div class="w-100 d-md-none"></div>

          <b-col class="pb-2">
            <b-form-input
              :value="characterName"
              placeholder="Character"
              @input="$emit('update:characterName', $event)"
            />
          </b-col>

          <b-col class="numeric-input wide pb-2">
            <b-input-group>
              <b-input-group-prepend>
                <div
                  :class="[cardType]"
                  class="icon-container text-center editor"
                  @click="updateCardType"
                >
                  <UnmatchedCardIcon
                    :cardType="cardType"
                    fitHeight="2em"
                    fitWidth="1.5em"
                    class="icon"
                  />
                </div>
              </b-input-group-prepend>
              <b-form-input
                v-if="!isScheme"
                type="number"
                :value="cardValue"
                @input="$emit('update:cardValue', parseInt($event))"
              />
            </b-input-group>
          </b-col>

          <b-col class="numeric-input wide pb-2">
            <b-input-group>
              <template v-slot:prepend>
                <b-input-group-text>B</b-input-group-text>
              </template>
              <b-form-input
                type="number"
                min="0"
                :value="boostValue"
                @input="$emit('update:boostValue', parseInt($event))"
              />
            </b-input-group>
          </b-col>
          <div class="w-100 d-md-none"></div>
        </b-form-row>
        <b-form-row>
          <b-col class="pb-2">
            <b-form-textarea
              placeholder="Effect"
              :state="isScheme && !basicText ? false : null"
              :value="basicText"
              @input="$emit('update:basicText', $event.trim())"
            />
          </b-col>
          <div class="w-100 d-sm-none"></div>

          <b-col v-if="!isScheme" class="pb-2">
            <b-form-textarea
              placeholder="Immediately"
              :value="immediateText"
              @input="$emit('update:immediateText', $event)"
            />
          </b-col>
          <div class="w-100 d-md-none"></div>
          <b-col v-if="!isScheme" class="pb-2">
            <b-form-textarea
              placeholder="During combat"
              :value="duringText"
              @input="$emit('update:duringText', $event)"
            />
          </b-col>
          <div class="w-100 d-sm-none"></div>

          <b-col v-if="!isScheme" class="pb-2">
            <b-form-textarea
              placeholder="After combat"
              :value="afterText"
              @input="$emit('update:afterText', $event)"
            />
          </b-col>
        </b-form-row>
      </div>
    </template>
    <template v-else>
      <b-row class="pb-2">
        <b-col class="numeric-input">
          <span>{{ cardQuantity }}&times;</span>
        </b-col>
        <b-col>
          <span>{{ cardTitle }}</span>
        </b-col>
        <b-col>
          <span>{{ characterName }}</span>
        </b-col>
        <b-col class="fit-content">
          <div
            :class="[cardType]"
            class="icon-container text-center"
            @click="updateCardType"
          >
            <UnmatchedCardIcon
              :cardType="cardType"
              fitHeight="2em"
              fitWidth=""
              class="icon"
            />
            <span v-if="!isScheme" class="pl-1">{{ cardValue }}</span>
          </div>
        </b-col>
        <b-col class="fit-content">
          <span><strong>Boost:</strong> {{ boostValue }}</span>
        </b-col>
      </b-row>
      <b-row>
        <b-col v-if="basicText">
          <p>
            {{ basicText }}
          </p>
        </b-col>
        <b-col v-if="!isScheme && immediateText">
          <p><strong>Immediately:</strong> {{ immediateText }}</p>
        </b-col>
        <b-col v-if="!isScheme && duringText">
          <p><strong>During combat:</strong> {{ duringText }}</p>
        </b-col>
        <b-col v-if="!isScheme && afterText">
          <p><strong>After combat:</strong> {{ afterText }}</p>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import UnmatchedCardIcon from "@/components/UnmatchedCardIcon.vue";

export default {
  name: "CompactCard",
  components: {
    UnmatchedCardIcon,
  },
  data: function () {
    return {};
  },
  props: {
    cardType: {
      type: String,
      default: "versatile",
    },
    cardValue: {
      type: Number,
      default: 2,
    },
    characterName: {
      type: String,
    },
    boostValue: {
      type: Number,
      default: 1,
    },
    cardTitle: {
      default: "Card title",
    },
    basicText: {
      default: "",
    },
    immediateText: {
      default: "",
    },
    duringText: {
      default: "",
    },
    afterText: {
      default: "",
    },
    cardQuantity: {
      type: Number,
      default: 1,
    },
    imageUrl: {
      default: "",
    },
    isEditable: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isScheme() {
      return this.cardType === "scheme";
    },
  },
  methods: {
    updateCardType: function () {
      if (this.isEditable) {
        var newType = "versatile";
        if (this.cardType === "versatile") {
          newType = "attack";
        } else if (this.cardType === "attack") {
          newType = "defence";
        } else if (this.cardType === "defence") {
          newType = "scheme";
        }

        this.$emit("update:cardType", newType);
      }
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="less">
@defence-blue: #2c76ac;
@attack-red: #dc3034;
@versatile-violet: #6c4e8d;
@scheme-yellow: #fcbd71;
.attack {
  background: @attack-red;
}

.defence {
  background: @defence-blue;
}

.versatile {
  background: @versatile-violet;
}

.scheme {
  background: @scheme-yellow;
}

.icon-container {
  display: flex;
  align-items: center;
  max-width: 4em;
  min-width: 4em;
  &.editor {
    max-width: 3em;
    min-width: 3em;
    &.attack,
    &.defence,
    &.versatile {
      border-radius: 0.25em 0 0 0.25em;
    }
  }
  border-radius: 0.25em;
  color: white;
  height: calc(1.5em + 0.75rem + 2px);
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  &.editor {
    cursor: pointer;
  }
}

.icon {
  width: 100%;
  padding-bottom: 1.5mm;
}

.numeric-input {
  max-width: 6em;
  &.wide {
    max-width: 8em;
  }
}

.fit-content {
  max-width: fit-content;
}

.drag-handle {
  color: #ced4da;
  @media (prefers-color-scheme: dark) {
    color: #515151;
  }
  cursor: grab;
}
</style>
